import { BulkAddCptCodesResponseApi, CptCodeOwnershipConflictApi } from "../pages/payerIntegration/types/providerIntegrationtypes";

export type SkippedCptRow = {
  providerId: number;
  codeType: string;
  code: string;
  issue: "Duplicate" | "Ownership conflict" | "Error";
};

const CSV_HEADER = "CodeType|Code|Issue";

const escapeCsvCell = (value: string) =>
  value.includes("|") || value.includes('"') || value.includes("\n")
    ? `"${value.replace(/"/g, '""')}"`
    : value;

const parseErrorEntry = (entry: string) => {
  const separator = entry.indexOf(":");
  if (separator <= 0) {
    return { code: entry.trim(), details: entry.trim() };
  }
  return {
    code: entry.slice(0, separator).trim(),
    details: entry.slice(separator + 1).trim(),
  };
};

export const buildSkippedRows = (
  providerId: number,
  codeType: string,
  result: BulkAddCptCodesResponseApi,
): SkippedCptRow[] => {
  const rows: SkippedCptRow[] = [];
  const duplicates = result.duplicates ?? (result as { Duplicates?: string[] }).Duplicates ?? [];
  const conflicts =
    result.conflicts ?? (result as { Conflicts?: CptCodeOwnershipConflictApi[] }).Conflicts ?? [];
  const errors = result.errors ?? (result as { Errors?: string[] }).Errors ?? [];

  duplicates.forEach((code) => {
    rows.push({
      providerId,
      codeType,
      code,
      issue: "Duplicate",
    });
  });

  conflicts.forEach((conflict) => {
    const code = conflict.cptCode ?? (conflict as { CptCode?: string }).CptCode ?? "";
    const ownerProviderId =
      conflict.ownerProviderId ??
      (conflict as { OwnerProviderId?: number }).OwnerProviderId ??
      providerId;
    rows.push({
      providerId: ownerProviderId,
      codeType,
      code,
      issue: "Ownership conflict",
    });
  });

  errors.forEach((entry) => {
    const parsed = parseErrorEntry(entry);
    rows.push({
      providerId,
      codeType,
      code: parsed.code,
      issue: "Error",
    });
  });

  return rows;
};

export const mergeAddResults = (
  results: BulkAddCptCodesResponseApi[],
): BulkAddCptCodesResponseApi => {
  const merged: BulkAddCptCodesResponseApi = {
    added: results.reduce(
      (sum, item) => sum + (item.added ?? (item as { Added?: number }).Added ?? 0),
      0,
    ),
    duplicates: results.flatMap(
      (item) => item.duplicates ?? (item as { Duplicates?: string[] }).Duplicates ?? [],
    ),
    conflicts: results.flatMap(
      (item) =>
        item.conflicts ?? (item as { Conflicts?: CptCodeOwnershipConflictApi[] }).Conflicts ?? [],
    ),
    errors: results.flatMap(
      (item) => item.errors ?? (item as { Errors?: string[] }).Errors ?? [],
    ),
  };

  const csvChunks: string[] = [];
  let resultFileName: string | null | undefined;

  for (const item of results) {
    const content =
      item.resultFileContent ??
      (item as { ResultFileContent?: string }).ResultFileContent;
    const name =
      item.resultFileName ?? (item as { ResultFileName?: string }).ResultFileName;

    if (!content) continue;
    if (!resultFileName && name) resultFileName = name;

    if (csvChunks.length === 0) {
      csvChunks.push(content);
    } else {
      const lines = content.split("\n");
      if (lines.length > 1) csvChunks.push(lines.slice(1).join("\n"));
    }
  }

  if (csvChunks.length > 0) {
    merged.resultFileName = resultFileName ?? "cpt-add-conflicts.csv";
    merged.resultFileContent = csvChunks.join("\n");
  }

  return merged;
};

export const buildAddResultSummary = (result: BulkAddCptCodesResponseApi) => {
  const duplicateCount =
    result.duplicates?.length ?? (result as { Duplicates?: string[] }).Duplicates?.length ?? 0;
  const conflictCount =
    result.conflicts?.length ?? (result as { Conflicts?: unknown[] }).Conflicts?.length ?? 0;
  const errorCount =
    result.errors?.length ?? (result as { Errors?: string[] }).Errors?.length ?? 0;
  const skippedCount = duplicateCount + conflictCount + errorCount;

  return {
    added: result.added ?? (result as { Added?: number }).Added ?? 0,
    duplicateCount,
    conflictCount,
    errorCount,
    skippedCount,
  };
};

export const formatAddResultMessage = (
  result: BulkAddCptCodesResponseApi,
  options?: { prefix?: string },
) => {
  const summary = buildAddResultSummary(result);
  const prefix = options?.prefix ?? "";

  if (summary.added > 0 && summary.skippedCount === 0) {
    return `${prefix}Added ${summary.added.toLocaleString()} code${summary.added === 1 ? "" : "s"}.`;
  }

  const skippedParts: string[] = [];
  if (summary.duplicateCount > 0) {
    skippedParts.push(
      `${summary.duplicateCount.toLocaleString()} duplicate${summary.duplicateCount === 1 ? "" : "s"}`,
    );
  }
  if (summary.conflictCount > 0) {
    skippedParts.push(
      `${summary.conflictCount.toLocaleString()} conflict${summary.conflictCount === 1 ? "" : "s"}`,
    );
  }
  if (summary.errorCount > 0) {
    skippedParts.push(
      `${summary.errorCount.toLocaleString()} error${summary.errorCount === 1 ? "" : "s"}`,
    );
  }

  const skippedText = skippedParts.join(", ");

  if (summary.added > 0) {
    return `${prefix}Added ${summary.added.toLocaleString()} code${summary.added === 1 ? "" : "s"}. Skipped ${skippedText}.`;
  }

  return `${prefix}No codes added. Skipped ${skippedText}.`;
};

export const skippedRowsToCsv = (rows: SkippedCptRow[]) => {
  const body = rows
    .map((row) => [row.codeType, row.code, row.issue].map(escapeCsvCell).join("|"))
    .join("\n");

  return `${CSV_HEADER}\n${body}`;
};

export const buildSkippedDownloadFilename = (
  providerCode: string,
  date = new Date(),
) => {
  const stamp = [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
    "-",
    String(date.getHours()).padStart(2, "0"),
    String(date.getMinutes()).padStart(2, "0"),
  ].join("");

  const safeCode = providerCode.trim() || "provider";
  return `${safeCode}-cpt-add-skipped-${stamp}.csv`;
};

export const downloadSkippedCptCodesCsv = (
  rows: SkippedCptRow[],
  filename: string,
) => {
  if (!rows.length) return;

  const blob = new Blob([skippedRowsToCsv(rows)], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

export const downloadTextFile = (content: string, filename: string) => {
  if (!content) return;
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || "cpt-import-conflicts.csv";
  link.click();
  URL.revokeObjectURL(url);
};
